/**
 * Agent-Studio API client: JSON calls and server-sent-event streams against
 * /api/agent-studio/{botId}/... (backend: src/controller/agent_studio/api.py).
 * The global fetch interceptor (src/index.js) attaches the Bearer token; the
 * org comes from the JWT. Streams use fetch (EventSource cannot send headers).
 */

const API = process.env.REACT_APP_API_BASE_URL;

// Mo or the server refused an addition over the plan's allowance: { botId, artifact, limit } (PlanLimits.js)
export const PLAN_LIMIT_EVENT = 'ub:studio-plan-limit';

export const studioUrl = (botId, path = '') => `${API}agent-studio/${encodeURIComponent(botId)}${path}`;

async function request(method, url, body) {
  const res = await fetch(url, {
    method,
    headers: body === undefined ? undefined : { 'Content-Type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!res.ok) {
    let message = String(res.status);
    let detail = null;
    try {
      const j = await res.json();
      // a structured refusal (e.g. the plan's allowance, PlanLimits.js) keeps its fields on err.detail
      if (j.detail && typeof j.detail === 'object') detail = j.detail;
      message = (detail ? detail.message : j.detail) || JSON.stringify(j);
    } catch (_) { /* keep status */ }
    const err = new Error(message);
    err.status = res.status;
    err.detail = detail;
    throw err;
  }
  return res.json();
}

export const studioApi = {
  get: (botId, path) => request('GET', studioUrl(botId, path)),
  post: (botId, path, body = {}) => request('POST', studioUrl(botId, path), body),
  patch: (botId, path, body) => request('PATCH', studioUrl(botId, path), body),
  del: (botId, path) => request('DELETE', studioUrl(botId, path)),
};

/**
 * Incremental SSE parser. Feed it decoded text chunks; it calls onEvent with
 * every parsed JSON `data:` payload (default event type only). Pure, so it is
 * unit-testable without streams.
 */
export function createSseParser(onEvent) {
  let buffer = '';
  return (chunk) => {
    buffer += chunk;
    let idx;
    // eslint-disable-next-line no-cond-assign
    while ((idx = buffer.indexOf('\n\n')) >= 0) {
      const block = buffer.slice(0, idx);
      buffer = buffer.slice(idx + 2);
      let data = '';
      let named = false;
      for (const line of block.split('\n')) {
        if (line.startsWith('event:')) named = true;
        else if (line.startsWith('data:')) data += line.slice(5).replace(/^ /, '');
      }
      if (!data || named) continue;
      try {
        onEvent(JSON.parse(data));
      } catch (_) { /* ignore malformed frames */ }
    }
  };
}

/** GET or POST a stream and dispatch its JSON events until it ends or is aborted. */
export async function streamEvents(url, { method = 'GET', body, signal } = {}, onEvent) {
  const res = await fetch(url, {
    method,
    headers: body === undefined ? { Accept: 'text/event-stream' } : { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
    body: body === undefined ? undefined : JSON.stringify(body),
    signal,
  });
  if (!res.ok || !res.body) {
    const err = new Error(String(res.status));
    err.status = res.status;
    throw err;
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  const feed = createSseParser(onEvent);
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    feed(decoder.decode(value, { stream: true }));
  }
}

export const newTestSessionId = () =>
  `studio-test-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;

export function formatCents(cents, lang) {
  if (cents == null) return '–';
  return `${Number(cents).toLocaleString(lang || undefined, { minimumFractionDigits: 2, maximumFractionDigits: 3 })} ct`;
}

export function formatSecs(ms, lang) {
  if (ms == null) return '–';
  return `${(ms / 1000).toLocaleString(lang || undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 })} s`;
}

/** `ts` in seconds (number) or an ISO string. */
export function timeAgo(ts, t, lang) {
  if (!ts) return '';
  const ms = typeof ts === 'number' ? ts * 1000 : Date.parse(ts);
  const d = Math.max(0, Math.floor((Date.now() - ms) / 1000));
  if (d < 60) return t('agent_studio_time_now');
  if (d < 3600) return t('agent_studio_time_minutes', { n: Math.floor(d / 60) });
  if (d < 86400) return t('agent_studio_time_hours', { n: Math.floor(d / 3600) });
  return new Date(ms).toLocaleDateString(lang || undefined);
}

export const pretty = (s) => (s ? String(s).replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase()) : '');
